import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Flame, CheckCircle2, ClipboardList, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import SymptomPanel from "@/components/SymptomPanel";
import TrackerTrends from "@/components/TrackerTrends";

const RANGES = [7, 14, 30];
const SERIES = [
  { key: "sleep_score", label: "Sleep", color: "#3B594C" },
  { key: "energy_score", label: "Energy", color: "#C87D65" },
  { key: "mood_score", label: "Mood", color: "#E0A96D" },
  { key: "wellbeing_score", label: "Wellbeing", color: "#527867" },
];

function fmtDate(iso) {
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white rounded-xl card-shadow px-3 py-2 border border-subtle">
      <div className="text-xs text-ink-muted mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="text-xs font-semibold" style={{ color: p.color }}>
          {p.value}
        </div>
      ))}
    </div>
  );
}

export default function Track() {
  const [view, setView] = useState("wellbeing");
  const [range, setRange] = useState(30);
  const [progress, setProgress] = useState(null);
  const [checkins, setCheckins] = useState([]);

  useEffect(() => {
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/checkins?range=${range}`).then((r) => setCheckins(r.data)).catch(() => {});
  }, [range]);

  const chartData = checkins.map((c) => ({
    date: fmtDate(c.date),
    sleep_score: c.sleep_score,
    energy_score: c.energy_score,
    mood_score: c.mood_score,
    wellbeing_score: c.wellbeing_score,
  }));

  return (
    <div className="px-5 pt-8 animate-fade-rise" data-testid="track-page">
      <p className="overline-label">Your progress</p>
      <h1 className="font-serif text-3xl font-bold text-ink mt-2">Track</h1>

      <div className="mt-4 flex items-center gap-2 bg-app-secondary rounded-full p-1 w-fit">
        {[["wellbeing", "Wellbeing"], ["symptoms", "Symptoms"], ["trackers", "Trackers"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`track-tab-${k}`}
            onClick={() => setView(k)}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${view === k ? "bg-white text-brand card-shadow" : "text-ink-muted"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === "symptoms" && <SymptomPanel />}
      {view === "trackers" && <TrackerTrends range={range} />}

      {view === "wellbeing" && (
      <>
      {progress && (
        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl p-5 card-shadow" data-testid="stat-completion">
            <TrendingUp size={18} className="text-brand" />
            <div className="font-serif text-3xl font-bold text-ink mt-2">{progress.completion_pct}%</div>
            <div className="text-ink-muted text-xs">30 day completion</div>
          </div>
          <div className="bg-white rounded-2xl p-5 card-shadow" data-testid="stat-days">
            <CheckCircle2 size={18} className="text-brand" />
            <div className="font-serif text-3xl font-bold text-ink mt-2">{progress.completed_count}<span className="text-lg text-ink-muted">/30</span></div>
            <div className="text-ink-muted text-xs">days completed</div>
          </div>
          <div className="bg-white rounded-2xl p-5 card-shadow" data-testid="stat-streak">
            <Flame size={18} className="text-accent-c" />
            <div className="font-serif text-3xl font-bold text-ink mt-2">{progress.streak}</div>
            <div className="text-ink-muted text-xs">current streak</div>
          </div>
          <div className="bg-white rounded-2xl p-5 card-shadow" data-testid="stat-checkins">
            <ClipboardList size={18} className="text-brand" />
            <div className="font-serif text-3xl font-bold text-ink mt-2">{progress.checkin_count}</div>
            <div className="text-ink-muted text-xs">check ins logged</div>
          </div>
        </div>
      )}

      {/* range toggle */}
      <div className="mt-6 flex items-center gap-2 bg-app-secondary rounded-full p-1 w-fit">
        {RANGES.map((r) => (
          <button
            key={r}
            data-testid={`range-${r}`}
            onClick={() => setRange(r)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              range === r ? "bg-white text-brand card-shadow" : "text-ink-muted"
            }`}
          >
            {r} days
          </button>
        ))}
      </div>

      {/* charts */}
      <div className="mt-5 space-y-4">
        {SERIES.map((s) => (
          <div key={s.key} className="bg-white rounded-2xl p-5 card-shadow" data-testid={`chart-${s.key}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-ink">{s.label}</span>
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
            </div>
            {chartData.length === 0 ? (
              <div className="h-[140px] flex items-center justify-center text-ink-muted text-sm">
                No check ins yet, log one to see your trend.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={chartData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5DFD5" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#88988E" }} tickLine={false} axisLine={false} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: "#88988E" }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey={s.key}
                    stroke={s.color}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: s.color }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        ))}
      </div>

      <p className="text-center text-ink-muted text-xs mt-6 px-4">
        These are your self reported scores. They reflect how you've felt, not a medical measurement or diagnosis.
      </p>
      </>
      )}
    </div>
  );
}
